/**
 * Lifecycle of a single todo item, mirroring the backend `TodoListMiddleware`
 * schema. Values are the exact wire strings the backend sends.
 */
enum TodoStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
}

/** A single planning todo emitted by Flora's `write_todos` tool. */
interface AgentTodo {
  content: string;
  status: TodoStatus;
}

/** The todo list to show above the message bar, and whose turn it came from. */
interface TodoBarState {
  todos: AgentTodo[] | null;
  /**
   * True when the todos were written by the current turn. A prior turn's
   * unfinished plan shown as a fallback is false: the running turn isn't
   * working on it, so it must not animate as live. Deliberate trade-off: a
   * "continue" turn after Stop resumes the old plan under a new turnId, so it
   * shows static until the agent writes its todos again. (Question-card answers
   * and tool approvals resume the same turn, so they stay live.)
   */
  isCurrentTurn: boolean;
}

/** Counts used by the collapsed todo bar header. */
interface TodoSummary {
  total: number;
  completed: number;
  /** The first in-progress todo's label (else the first pending one), or null. */
  activeLabel: string | null;
}

/** Props for `FloraTodoBar`. */
interface FloraTodoBarProps {
  /** The current turn's todo list. The bar renders nothing when empty. */
  todos: AgentTodo[];
  /**
   * Whether the owning turn is still streaming. Drives the spinning indicator
   * on the in-progress row and the header pulse; a settled turn shows static
   * icons so a never-terminated `in_progress` status cannot spin forever.
   */
  isLive?: boolean;
}

export { TodoStatus };
export type { AgentTodo, FloraTodoBarProps, TodoBarState, TodoSummary };
